import React, { Fragment, Component, MouseEvent } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { Link, Menu, MenuItem, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import { GetFeedGroupFragment, Group } from 'api';
import { generateLink } from 'Links';

const SHOWN_GROUPS = 2;

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'inline-block',
    },
    moreText: {
      display: 'inline-block',
      marginLeft: 4,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    menuItem: {
      '&:hover, &:focus': {
        textDecoration: 'underline',
        backgroundColor: theme.palette.common.white,
      },
    },
  });

interface IProps {
  groups: GetFeedGroupFragment[];
}

interface IState {
  anchorEl: HTMLElement;
}

interface IPropsEnhanced extends IProps, WithTranslation, WithStyles<typeof styles> {}

const enhancers = compose<IPropsEnhanced, IProps>(withTranslation(), withStyles(styles));

class Groups extends Component<IPropsEnhanced, IState> {
  state: IState = {
    anchorEl: null,
  };

  handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  renderGroupsList() {
    const { groups } = this.props;

    const lastIndex = Math.min(groups.length - 1, SHOWN_GROUPS - 1);
    const firstTwo = groups.slice(0, SHOWN_GROUPS);

    return firstTwo.map(({ id, title, color_map }, index) => (
      <Fragment key={id}>
        <Link component={generateLink(`/group/${id}`)}>{title}</Link>
        {index !== lastIndex && <span>, </span>}
      </Fragment>
    ));
  }

  renderMenuItem = (group: Group) => {
    const { classes } = this.props;
    const { id, title } = group;

    const itemProps = {
      key: id,
      component: generateLink(`/group/${id}`),
      className: classes.menuItem,
    };

    return <MenuItem {...itemProps}>{title}</MenuItem>;
  };

  renderGroupsMenu() {
    const { groups } = this.props;
    const { anchorEl } = this.state;

    const remainingGroups = groups.slice(SHOWN_GROUPS);

    return (
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={this.handleClose}>
        <div>{remainingGroups.map(this.renderMenuItem)}</div>
      </Menu>
    );
  }

  renderMoreLink() {
    const { classes, groups, t } = this.props;

    return (
      <Typography component="span" className={classes.moreText} onClick={this.handleClick}>
        {t('content:xMoreGroups', { groups: groups.length - SHOWN_GROUPS })}
      </Typography>
    );
  }

  render() {
    const { classes, groups } = this.props;

    if (!groups || !groups.length) {
      return null;
    }

    const needsMore = groups.length > SHOWN_GROUPS;

    return (
      <Typography className={classes.root}>
        {this.renderGroupsList()}
        {needsMore && this.renderMoreLink()}
        {this.renderGroupsMenu()}
      </Typography>
    );
  }
}

export default enhancers(Groups);
